/**
 * Attribute name formatting utilities for audit logs.
 */

import {
  ALL_CAPS_WORDS,
  RAW_ATTRIBUTE_MAPPINGS,
  ABBREVIATION_EXPANSIONS,
  CAMPAIGN_ATTRIBUTE_MAPPINGS,
} from './constants';

/**
 * Convert attribute names from snake_case or camelCase to user-friendly Title Case.
 *
 * Features:
 * - Converts snake_case to Title Case (first_name → "First Name")
 * - Converts camelCase to Title Case (firstName → "First Name")
 * - Preserves known acronyms (API, ID, URL, etc.) from ALL_CAPS_WORDS
 * - Handles special number+letter combinations (email2fa → "Email 2FA")
 * - Applies campaign-specific mappings for nested paths
 * - Handles nested paths (user.settings.theme → "User: Settings: Theme")
 *
 * @param name - The raw attribute name to format
 * @returns The formatted, human-readable attribute name
 *
 * @example
 * formatAttributeName('first_name') // "First Name"
 * formatAttributeName('apiKey') // "API Key"
 * formatAttributeName('user_id') // "User ID"
 * formatAttributeName('email2_fa_enabled') // "Email 2FA Enabled"
 */
export const formatAttributeName = (name: string): string => {
  // Check for raw attribute name mappings first (before formatting)
  const nameLower = name.toLowerCase();
  if (RAW_ATTRIBUTE_MAPPINGS[name] != null) {
    return RAW_ATTRIBUTE_MAPPINGS[name];
  }
  if (RAW_ATTRIBUTE_MAPPINGS[nameLower] != null) {
    return RAW_ATTRIBUTE_MAPPINGS[nameLower];
  }

  // Handle nested paths (e.g., "user_flags.aggressive_positivity_available")
  if (name.includes('.')) {
    const parts = name.split('.');
    // For Campaign object type, skip the "campaign" prefix and only show the child name
    if (parts.length > 1 && parts[0].toLowerCase() === 'campaign') {
      const remaining = parts.slice(1);
      // Also skip "fsm" level for Campaign
      if (remaining.length > 1 && remaining[0].toLowerCase() === 'fsm') {
        return formatAttributeName(remaining.slice(1).join('.'));
      }
      return formatAttributeName(remaining.join('.'));
    }
    // Skip "fsm" prefix when it's the first part
    if (parts.length > 1 && parts[0].toLowerCase() === 'fsm') {
      return formatAttributeName(parts.slice(1).join('.'));
    }
    return parts.map((part) => formatAttributeName(part)).join(': ');
  }

  // Replace underscores and hyphens with spaces
  let formatted = name.replace(/[_-]/g, ' ');
  // Split by camelCase boundaries (lowercase followed by uppercase)
  formatted = formatted.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Handle special case: words ending with number+letters that form a known acronym
  // e.g., "email2fa" should become "email 2FA", not "email 2 fa"
  formatted = formatted
    .split(' ')
    .map((word) => {
      // Check for pattern like "word2fa" where "2fa" is a known acronym
      const numberLetterMatch = word.match(/^(.+?)(\d+[A-Za-z]+)$/);
      if (numberLetterMatch != null) {
        const [, prefix, suffix] = numberLetterMatch;
        const suffixUpper = suffix.toUpperCase();
        const capsMatch = ALL_CAPS_WORDS.find((capsWord) => capsWord.toUpperCase() === suffixUpper);
        if (capsMatch != null) {
          return `${prefix} ${capsMatch}`;
        }
      }
      return word;
    })
    .join(' ');

  // Handle special case: adjacent words where "number" + "letters" form a known acronym
  // e.g., "email2 fa" should become "email 2FA"
  const words = formatted.split(' ');
  const mergedWords: string[] = [];
  let i = 0;
  while (i < words.length) {
    const currentWord = words[i];
    const nextWord = words[i + 1];

    // Check if current word ends with digits and next word could form an acronym
    if (nextWord != null) {
      const digitSuffixMatch = currentWord.match(/^(.+?)(\d+)$/);
      if (digitSuffixMatch != null) {
        const [, prefix, digits] = digitSuffixMatch;
        const potentialAcronym = digits + nextWord;
        const potentialUpper = potentialAcronym.toUpperCase();
        const capsMatch = ALL_CAPS_WORDS.find((capsWord) => capsWord.toUpperCase() === potentialUpper);
        if (capsMatch != null) {
          mergedWords.push(prefix, capsMatch);
          i += 2; // Skip both words
          continue;
        }
      }
    }
    mergedWords.push(currentWord);
    i++;
  }
  formatted = mergedWords.join(' ');

  // Split numbers from letters (e.g., "Key2" -> "Key 2") - but not if already handled above
  formatted = formatted.replace(/([A-Za-z])(\d)(?!\d*[A-Za-z])/g, '$1 $2');

  // Capitalize first letter of each word, but preserve all-caps words
  let result = formatted
    .split(' ')
    .filter((word) => word.length > 0)
    .map((word) => {
      const upperWord = word.toUpperCase();
      // Check if word matches exactly one of the all-caps words (case-insensitive, same length)
      const capsMatch = ALL_CAPS_WORDS.find(
        (capsWord) => capsWord.toUpperCase() === upperWord && capsWord.length === word.length,
      );
      if (capsMatch != null) {
        // Check if the all-caps word should be expanded (e.g., TS -> Technical Support)
        if (ABBREVIATION_EXPANSIONS[capsMatch] != null) {
          return ABBREVIATION_EXPANSIONS[capsMatch];
        }
        return capsMatch;
      }
      const titleCased = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      // Check if word should be expanded (e.g., Attr -> Attribute)
      if (ABBREVIATION_EXPANSIONS[titleCased] != null) {
        return ABBREVIATION_EXPANSIONS[titleCased];
      }
      return titleCased;
    })
    .join(' ');

  // Apply campaign-specific attribute mappings
  if (CAMPAIGN_ATTRIBUTE_MAPPINGS[result] != null) {
    result = CAMPAIGN_ATTRIBUTE_MAPPINGS[result];
  }

  return result;
};

/**
 * Permission label mappings from internal permission strings to user-friendly labels.
 * These match the labels used in the Team Members page and permission management UI.
 *
 * @see https://github.com/customerio/ui - hydra-permissions/metadata.ts
 */
const PERMISSION_LABELS: Record<string, string> = {
  // Account-level permissions
  'account/view': 'View Account',
  'account/edit': 'Edit Account',
  'account/billing/view': 'View Billing',
  'account/billing/edit': 'Manage Billing',
  'account/team_members/view': 'View Team Members',
  'account/team_members/edit': 'Manage Team Members',
  'account/workspaces/create': 'Create Workspaces',
  'account/api_credentials/view': 'View API Credentials',
  'account/api_credentials/edit': 'Manage API Credentials',
  'account/support_access/view': 'View Support Access',
  'account/support_access/edit': 'Manage Support Access',
  'account/cookie_settings/view': 'View Cookie Settings',
  'account/cookie_settings/edit': 'Manage Cookie Settings',
  'account/pipelines/enable': 'Enable Integrations',
  'account/journeys/enable': 'Enable Journeys',

  // Workspace settings
  'journeys/workspace_settings/view': 'View Workspace Settings',
  'journeys/workspace_settings/edit': 'Edit Workspace Settings',

  // Imports & Exports
  'journeys/imports/view': 'View Imports',
  'journeys/imports/create': 'Create Imports',
  'journeys/imports/edit': 'Edit Imports',
  'journeys/imports/delete': 'Delete Imports',
  'journeys/exports/view': 'View Exports',
  'journeys/exports/create': 'Create Exports',
  'journeys/exports/edit': 'Edit Exports',
  'journeys/exports/delete': 'Delete Exports',

  // Message settings
  'journeys/message_settings/view': 'View Message Settings',
  'journeys/message_settings/edit': 'Edit Message Settings',
  'journeys/sending_options/view': 'View Sending Options',
  'journeys/sending_options/edit': 'Edit Sending Options',

  // Campaigns
  'journeys/campaigns/view': 'View Campaigns',
  'journeys/campaigns/create': 'Create Campaigns',
  'journeys/campaigns/edit': 'Edit Campaigns',
  'journeys/campaigns/delete': 'Delete Campaigns',

  // Broadcasts
  'journeys/broadcasts/view': 'View Broadcasts',
  'journeys/broadcasts/create': 'Create Broadcasts',
  'journeys/broadcasts/edit': 'Edit Broadcasts',
  'journeys/broadcasts/delete': 'Delete Broadcasts',

  // Newsletters
  'journeys/newsletters/view': 'View Newsletters',
  'journeys/newsletters/create': 'Create Newsletters',
  'journeys/newsletters/edit': 'Edit Newsletters',
  'journeys/newsletters/delete': 'Delete Newsletters',

  // Transactional Messages
  'journeys/transactional_messages/view': 'View Transactional Messages',
  'journeys/transactional_messages/create': 'Create Transactional Messages',
  'journeys/transactional_messages/edit': 'Edit Transactional Messages',
  'journeys/transactional_messages/delete': 'Delete Transactional Messages',

  // Anonymous Messages
  'journeys/in_app_broadcasts/view': 'View Anonymous In-App Messages',
  'journeys/in_app_broadcasts/create': 'Create Anonymous In-App Messages',
  'journeys/in_app_broadcasts/edit': 'Edit Anonymous In-App Messages',
  'journeys/in_app_broadcasts/delete': 'Delete Anonymous In-App Messages',

  // People
  'journeys/people/view': 'View People',
  'journeys/people/create': 'Create People',
  'journeys/people/edit': 'Edit People',
  'journeys/people/delete': 'Delete People',

  // Object Types
  'journeys/object_types/view': 'View Object Types',
  'journeys/object_types/create': 'Create Object Types',
  'journeys/object_types/edit': 'Edit Object Types',
  'journeys/object_types/delete': 'Delete Object Types',

  // Objects
  'journeys/objects/view': 'View Objects',
  'journeys/objects/create': 'Create Objects',
  'journeys/objects/edit': 'Edit Objects',
  'journeys/objects/delete': 'Delete Objects',

  // Object Relationships
  'journeys/object_relationships/view': 'View Object Relationships',
  'journeys/object_relationships/create': 'Create Object Relationships',
  'journeys/object_relationships/edit': 'Edit Object Relationships',
  'journeys/object_relationships/delete': 'Delete Object Relationships',

  // Messages
  'journeys/messages/send': 'Send Messages',
  'journeys/messages/edit': 'Edit Messages',
  'journeys/test_events/send': 'Send Test Events',

  // Forms
  'journeys/forms/view': 'View Forms',
  'journeys/forms/create': 'Create Forms',
  'journeys/forms/edit': 'Edit Forms',
  'journeys/forms/delete': 'Delete Forms',

  // SQL Syncs
  'journeys/sql_syncs/view': 'View SQL Syncs',
  'journeys/sql_syncs/create': 'Create SQL Syncs',
  'journeys/sql_syncs/edit': 'Edit SQL Syncs',
  'journeys/sql_syncs/delete': 'Delete SQL Syncs',

  // JS Snippets
  'journeys/js_snippets/view': 'View JS Snippets',
  'journeys/js_snippets/create': 'Create JS Snippets',
  'journeys/js_snippets/edit': 'Edit JS Snippets',
  'journeys/js_snippets/delete': 'Delete JS Snippets',

  // Segment Integration
  'journeys/segment_integration/view': 'View Segment Integration',
  'journeys/segment_integration/create': 'Create Segment Integration',
  'journeys/segment_integration/edit': 'Edit Segment Integration',
  'journeys/segment_integration/delete': 'Delete Segment Integration',

  // mParticle Integration
  'journeys/mParticle/edit': 'Edit mParticle Integration',
  'journeys/mParticle/delete': 'Delete mParticle Integration',

  // Reporting Webhooks
  'journeys/reporting_webhooks/edit': 'Edit Reporting Webhooks',
  'journeys/reporting_webhooks/delete': 'Delete Reporting Webhooks',

  // Audiences Integration
  'journeys/audiences_integration/view': 'View Audiences Integration',
  'journeys/audiences_integration/create': 'Create Audiences Integration',
  'journeys/audiences_integration/edit': 'Edit Audiences Integration',
  'journeys/audiences_integration/delete': 'Delete Audiences Integration',

  // Logs
  'journeys/logs/view': 'View Logs',

  // Segments
  'journeys/segments/view': 'View Segments',
  'journeys/segments/create': 'Create Segments',
  'journeys/segments/edit': 'Edit Segments',
  'journeys/segments/delete': 'Delete Segments',

  // Subscription Center
  'journeys/subscription_center/view': 'View Subscription Center',
  'journeys/subscription_center/create': 'Create Subscription Center',
  'journeys/subscription_center/edit': 'Edit Subscription Center',
  'journeys/subscription_center/delete': 'Delete Subscription Center',
  'journeys/subscription_center/enable': 'Enable Subscription Center',
  'journeys/subscriptions/edit': 'Edit Subscriptions',

  // Collections
  'journeys/collections/view': 'View Collections',
  'journeys/collections/create': 'Create Collections',
  'journeys/collections/edit': 'Edit Collections',
  'journeys/collections/delete': 'Delete Collections',

  // Snippets
  'journeys/snippets/view': 'View Snippets',
  'journeys/snippets/create': 'Create Snippets',
  'journeys/snippets/edit': 'Edit Snippets',
  'journeys/snippets/delete': 'Delete Snippets',

  // Layouts
  'journeys/layouts/view': 'View Layouts',
  'journeys/layouts/create': 'Create Layouts',
  'journeys/layouts/edit': 'Edit Layouts',
  'journeys/layouts/delete': 'Delete Layouts',

  // Email Message Settings
  'journeys/email_message_settings/view': 'View Email Settings',
  'journeys/email_message_settings/create': 'Create Email Settings',
  'journeys/email_message_settings/edit': 'Edit Email Settings',
  'journeys/email_message_settings/delete': 'Delete Email Settings',

  // Slack Message Settings
  'journeys/slack_message_settings/view': 'View Slack Settings',
  'journeys/slack_message_settings/create': 'Create Slack Settings',
  'journeys/slack_message_settings/edit': 'Edit Slack Settings',
  'journeys/slack_message_settings/delete': 'Delete Slack Settings',

  // Twilio Message Settings
  'journeys/twillio_message_settings/view': 'View Twilio Settings',
  'journeys/twillio_message_settings/create': 'Create Twilio Settings',
  'journeys/twillio_message_settings/edit': 'Edit Twilio Settings',
  'journeys/twillio_message_settings/delete': 'Delete Twilio Settings',

  // Push Message Settings
  'journeys/push_message_settings/view': 'View Push Settings',
  'journeys/push_message_settings/create': 'Create Push Settings',
  'journeys/push_message_settings/edit': 'Edit Push Settings',
  'journeys/push_message_settings/delete': 'Delete Push Settings',

  // In-App Message Settings
  'journeys/in_app_message_settings/view': 'View In-App Settings',
  'journeys/in_app_message_settings/create': 'Create In-App Settings',
  'journeys/in_app_message_settings/edit': 'Edit In-App Settings',
  'journeys/in_app_message_settings/delete': 'Delete In-App Settings',

  // Language Settings
  'journeys/language_settings/view': 'View Language Settings',
  'journeys/language_settings/create': 'Create Language Settings',
  'journeys/language_settings/edit': 'Edit Language Settings',
  'journeys/language_settings/delete': 'Delete Language Settings',

  // Ad Audiences
  'journeys/ad_audience/view': 'View Ad Audiences',
  'journeys/ad_audience/create': 'Create Ad Audiences',
  'journeys/ad_audience/edit': 'Edit Ad Audiences',
  'journeys/ad_audience/delete': 'Delete Ad Audiences',

  // Assets
  'journeys/assets/view': 'View Assets',
  'journeys/assets/create': 'Create Assets',
  'journeys/assets/edit': 'Edit Assets',
  'journeys/assets/delete': 'Delete Assets',

  // Merge Options & Attribute Metadata
  'journeys/merge_options/edit': 'Edit Merge Options',
  'journeys/attribute_metadata/edit': 'Edit Attribute Metadata',

  // Data Access
  'journeys/attribute_data/view': 'View Attribute Data',
  'journeys/sensitive_data/view': 'View Sensitive Data',

  // Suppressions
  'journeys/suppressions/create': 'Create Suppressions',

  // Integrations
  'journeys/integrations/view': 'View Integrations',
  'journeys/integrations/create': 'Create Integrations',
  'journeys/integrations/edit': 'Edit Integrations',
  'journeys/integrations/delete': 'Delete Integrations',

  // Metric Exports
  'journeys/metric_exports/view': 'View Metric Exports',
  'journeys/metric_exports/create': 'Create Metric Exports',

  // Parcel (Design Studio)
  'parcel/emails/view': 'View Design Studio Emails',
  'parcel/emails/create': 'Create Design Studio Emails',
  'parcel/emails/edit': 'Edit Design Studio Emails',
  'parcel/emails/delete': 'Delete Design Studio Emails',
  'parcel/components/view': 'View Design Studio Components',
  'parcel/components/create': 'Create Design Studio Components',
  'parcel/components/edit': 'Edit Design Studio Components',
  'parcel/components/delete': 'Delete Design Studio Components',
  'parcel/previews/send_test': 'Send Design Studio Test Previews',

  // Pipelines (Data Pipelines)
  'pipelines/sources/view': 'View Data Sources',
  'pipelines/sources/create': 'Create Data Sources',
  'pipelines/sources/edit': 'Edit Data Sources',
  'pipelines/sources/delete': 'Delete Data Sources',
  'pipelines/destinations/view': 'View Data Destinations',
  'pipelines/destinations/create': 'Create Data Destinations',
  'pipelines/destinations/edit': 'Edit Data Destinations',
  'pipelines/destinations/delete': 'Delete Data Destinations',
  'pipelines/suppressions/view': 'View Pipeline Suppressions',
  'pipelines/suppressions/create': 'Create Pipeline Suppressions',
  'pipelines/suppressions/delete': 'Delete Pipeline Suppressions',
  'pipelines/attribute_data/view': 'View Pipeline Attribute Data',
  'pipelines/sensitive_data/view': 'View Pipeline Sensitive Data',
};

/**
 * Format permission values to user-friendly labels.
 * Uses the PERMISSION_LABELS mapping for known permissions, falls back to
 * formatting each part of the permission string for unknown permissions.
 *
 * @example
 * formatPermissionValue('account/billing/view') // 'View Billing'
 * formatPermissionValue('journeys/campaigns/edit') // 'Edit Campaigns'
 * formatPermissionValue('unknown/permission/value') // 'Unknown | Permission | Value'
 */
export const formatPermissionValue = (permission: string): string => {
  // Check for exact match in permission labels
  const label = PERMISSION_LABELS[permission.toLowerCase()];
  if (label != null) {
    return label;
  }

  // Fallback: format each part of the permission string
  if (!permission.includes('/')) {
    return permission;
  }
  const parts = permission.split('/');
  if (parts.length < 2) {
    return permission;
  }
  return parts.map((part) => formatAttributeName(part.trim())).join(' | ');
};

/**
 * Standard role name mappings from internal role identifiers to user-friendly labels.
 * These match the labels used in the Team Members page of the UI.
 *
 * @see https://github.com/customerio/ui - hydra-permissions/roles.ts
 */
const STANDARD_ROLE_LABELS: Record<string, string> = {
  admin: 'Account Admin',
  manager: 'Workspace Admin',
  author: 'Author',
  'author-redact-on': 'Author with attributes hidden',
  'author-redact-sensitive': 'Author with sensitive attributes hidden',
  'author-redact-all': 'Author with attributes hidden',
  viewer: 'Viewer',
  'viewer-redact-on': 'Viewer with attributes hidden',
  'viewer-redact-sensitive': 'Viewer with sensitive attributes hidden',
  'viewer-redact-all': 'Viewer with attributes hidden',
};

/**
 * Format a role name to a user-friendly label.
 * Standard roles (admin, author, viewer, etc.) are mapped to friendly labels.
 * Custom roles are returned as-is since they already have user-defined names.
 *
 * @param roleName - The role name to format
 * @returns The user-friendly role label
 *
 * @example
 * formatRoleName('admin') // 'Account Admin'
 * formatRoleName('author-redact-sensitive') // 'Author with sensitive attributes hidden'
 * formatRoleName('My Custom Role') // 'My Custom Role'
 */
export const formatRoleName = (roleName: string): string => {
  const standardLabel = STANDARD_ROLE_LABELS[roleName.toLowerCase()];
  return standardLabel ?? roleName;
};

/**
 * Check if a word is an acronym that should be preserved in ALL_CAPS.
 */
const isAcronymWord = (word: string): boolean => {
  const upperWord = word.toUpperCase();
  return ALL_CAPS_WORDS.some((capsWord) => capsWord.toUpperCase() === upperWord && capsWord.length === word.length);
};

/**
 * Multi-word phrases that should be preserved exactly as written (not lowercased).
 * These are proper nouns or product names that should maintain their casing.
 *
 * Add to this list when:
 * - A product name spans multiple words (e.g., "Design Studio")
 * - A brand name requires specific casing
 * - NOT needed for single-word acronyms (use ALL_CAPS_WORDS instead)
 */
const PRESERVED_PHRASES = ['Design Studio', 'API-triggered broadcast'];

/**
 * Lowercase a string but preserve ALL_CAPS words (like API, SMS, ID, etc.)
 * and multi-word phrases (like "Design Studio").
 * Used for Activity column display where we want lowercase but still respect acronyms and proper nouns.
 *
 * @example
 * lowercasePreservingAcronyms('API Credentials') // 'API credentials'
 * lowercasePreservingAcronyms('Campaign') // 'campaign'
 * lowercasePreservingAcronyms('SMS Settings') // 'SMS settings'
 * lowercasePreservingAcronyms('Design Studio Node') // 'Design Studio element'
 */
export const lowercasePreservingAcronyms = (text: string): string => {
  // First, replace preserved phrases with placeholders to protect them
  let result = text;
  const placeholders: Map<string, string> = new Map();

  for (const phrase of PRESERVED_PHRASES) {
    const regex = new RegExp(phrase, 'gi');
    if (regex.test(result)) {
      const placeholder = `__PRESERVED_${placeholders.size}__`;
      placeholders.set(placeholder, phrase);
      result = result.replace(new RegExp(phrase, 'gi'), placeholder);
    }
  }

  // Process individual words
  result = result
    .split(' ')
    .map((word) => {
      // Check if this is a placeholder
      if (word.startsWith('__PRESERVED_') && word.endsWith('__')) {
        return placeholders.get(word) ?? word;
      }
      return isAcronymWord(word) ? word.toUpperCase() : word.toLowerCase();
    })
    .join(' ');

  return result;
};

/**
 * Capitalize the first letter of a string while preserving ALL_CAPS words.
 * Used for starting sentences with proper capitalization.
 *
 * @example
 * capitalizePreservingAcronyms('API credentials') // 'API credentials'
 * capitalizePreservingAcronyms('campaign') // 'Campaign'
 * capitalizePreservingAcronyms('api key') // 'API key'
 */
export const capitalizePreservingAcronyms = (text: string): string => {
  return text
    .split(' ')
    .map((word, index) => {
      // Check if it's an acronym - always preserve as uppercase
      if (isAcronymWord(word)) {
        return word.toUpperCase();
      }
      // Capitalize first word, lowercase the rest
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word.toLowerCase();
    })
    .join(' ');
};
